import { Table as BaseTable, TableProps } from '@/components/table'
import { Query } from './table_query'
import { AccommodationContactsQuery } from './__generated__/table_query'
import { useQuery } from '@apollo/client'
import { Inline as Error } from '@/components/error'
import { Inline as Loading } from '@/components/loading'
import { useState } from 'react'
import { Badge } from '@/components/event_contact_type/badge'
import { AccommodationContactFilters } from '@/types'
import { contactTypes } from './contact_types'
import { useTable } from '@/helpers/useTable'
import { Button } from '../base/button'
import { dedupe } from '@/helpers/dedupe'
import { EditDialog } from './edit_dialog'
import { NewDialog } from './new_dialog'

interface Props extends TableProps {
  accommodationId: string
  createable?: boolean
  updateable?: boolean
}

export const Table = ({ pageSize = 10, accommodationId, createable = false, updateable = false, ...props }: Props) => {
  const { data, loading, error, refetch, fetchMore } = useQuery<AccommodationContactsQuery>(
    Query,
    { variables: { accommodationId, first: pageSize } }
  )
  const {
    pageInfo,
    openNew,
    setOpenNew,
    openEdit,
    setOpenEdit
  } = useTable({ pageInfo: data?.accommodations.nodes[0].accommodationContacts.pageInfo })
  const [accommodationContactId, setAccommodationContactId] = useState<string>()

  const editAccommodationContact = (id: string) => {
    if (!updateable) return

    setAccommodationContactId(id)
    setOpenEdit(true)
  }

  if (error) { return <Error message={error.message} /> }
  if (loading || !data) { return <Loading /> }

  return (
    <>
      <BaseTable<AccommodationContactFilters>
        {...props}
        actionButton={createable ? <Button color="green" onClick={() => setOpenNew(true)}>Add</Button> : undefined}
        headings={[{ name: 'Type' }, { name: 'Name' }, { name: 'Email' }, { name: 'Phone' }]}
        moreToFetch={pageInfo.current?.hasNextPage}
        refetch={refetch}
        fetchMore={() => fetchMore({
          variables: { after: pageInfo.current?.endCursor },
          updateQuery(previousData, { fetchMoreResult }) {
            if (Object.keys(previousData).length === 0) return fetchMoreResult
            fetchMoreResult.accommodations.nodes[0].accommodationContacts.nodes = dedupe([
              ...previousData.accommodations.nodes[0].accommodationContacts.nodes,
              ...fetchMoreResult.accommodations.nodes[0].accommodationContacts.nodes
            ])
            return { ...fetchMoreResult }
          }
        })}
        filters={[
          {
            index: 0,
            setValue: (set, value) => set(f => ({ ...f, contactTypeEq: value })),
            type: 'select',
            options: Object.keys(contactTypes).map(ct => ({
              value: ct,
              display: <Badge eventContact={{ name: contactTypes[ct].name, color: contactTypes[ct].color }} />
            }))
          },
          {
            index: 1,
            setValue: (set, value) => set(f => ({ ...f, nameCont: value })),
            type: 'text',
            placeholder: 'What did you call them?'
          },
          {
            index: 2,
            setValue: (set, value) => set(f => ({ ...f, emailCont: value })),
            type: 'text',
            placeholder: 'Did you email them?'
          },
          {
            index: 3,
            setValue: (set, value) => set(f => ({ ...f, phoneCont: value })),
            type: 'text',
            placeholder: 'Did you ring them?'
          },
        ]}
        rows={data.accommodations.nodes[0].accommodationContacts.nodes.map(accommodationContact => (
          {
            id: accommodationContact.id,
            columns: [
              <Badge
                eventContact={{
                  name: contactTypes[accommodationContact.contactType].name,
                  color: contactTypes[accommodationContact.contactType].color
                }}
              />,
              accommodationContact.name,
              accommodationContact.email,
              accommodationContact.phone,
            ]
          }
        ))}
        onRowClick={editAccommodationContact}
      />
      { accommodationContactId &&
      <EditDialog
        accommodationId={accommodationId}
        id={accommodationContactId}
        open={openEdit}
        setOpen={setOpenEdit}
      /> }
      <NewDialog id={accommodationId} open={openNew} setOpen={setOpenNew} />
    </>
  )
}
